import { v4 as uuid } from 'uuid';

import { type FloorGameCardProps } from '@pkg/components/FloorGameCard';
import { legacyConvertCurrency } from '@pkg/components/Sections/Groups/helpers';
import {
    EthTokenStardard,
    FloorGameBlockchain,
    FloorGameCrypto,
    FloorGameMarketplace,
    type NftAsset,
    type NftCollection,
    type NftContract,
    type NftCreator,
    type NftMarket,
    NftStatus,
    type NftValue,
    type YatUser,
} from '@pkg/types/floorGame';
import { trimDecimals } from '@pkg/utils';
import { determineTier } from '@pkg/utils/floorGame';

import { type GroupDataItem, type GroupItem } from '@app/client/common/structs/groups/groups';
import { Currency, type CurrencyConversionRate } from '@app/client/common/structs/nft/currencies';
import config from '@app/config';

interface GetFloorGameOptions {
    item: GroupItem;
    owner: GroupDataItem['owner'];
    lastSale?: number;
    currencyConversionRate: CurrencyConversionRate;
}

export function getFloorGameProps(groupCardProps: GetFloorGameOptions): FloorGameCardProps {
    const {
        image,
        animation_url,
        name: tokenName,
        slug,
        token_id,
        contract_address,
        owner_address,
        collection_image,
        collection_name: collectionName,
    } = groupCardProps.item.item_type.Nft;

    function decompressEthAddress(compressedAddress: string) {
        return compressedAddress.startsWith('0x') ? compressedAddress : `0x${compressedAddress}`;
    }

    function getTokenId(random = false): NonNullable<FloorGameCardProps['tokenId']> {
        const randomId = uuid();

        if (random) return randomId;

        return token_id ? token_id : randomId;
    }

    function getId(random = false): FloorGameCardProps['id'] {
        const tokenId = getTokenId(random);
        return `floor-game_${slug}-${tokenId}`;
    }

    function getName(random = false): FloorGameCardProps['name'] {
        const tokenId = random ? uuid() : tokenName || token_id;
        return `#${tokenId}`;
    }

    function getStatus(): NftStatus {
        return NftStatus.Unlisted;
    }

    function getCollectionSlug(): NftCollection['slug'] {
        return groupCardProps.item.item_type.Nft.slug ?? '';
    }

    function getCollectionUrl(): NftCollection['thumbnail'] {
        return `https://opensea.io/collection/${getCollectionSlug()}`;
    }

    function getCollectionImage() {
        return groupCardProps.item.item_type.Nft.collection_image ?? undefined;
    }

    function getOwner(): YatUser {
        return {
            id: groupCardProps.owner.user_id ?? '',
            yat: {
                primary: groupCardProps.owner.primary_yat ?? '',
                yats: [],
                followers: {
                    count: 0,
                    dailyGrowth: 0,
                },
            },
            profileUrl: `${config.publicUrl}/${groupCardProps.owner.primary_yat}`,
            wallet: {
                primary: decompressEthAddress(owner_address ?? ''),
                wallets: [
                    {
                        id: decompressEthAddress(owner_address ?? ''),
                        currency: FloorGameCrypto.Ethereum,
                    },
                ],
            },
            twitter: {
                handle: groupCardProps.owner.twitter ?? '',
                followers: {
                    count: 0,
                    dailyGrowth: 0,
                },
            },
        };
    }

    function getContract(): NftContract {
        const creator: NftCreator = {
            name: collectionName ?? '',
            thumbnail: getCollectionImage(),
            url: getCollectionUrl(),
            // TODO: Do we need this?
            // slug: getCollectionSlug(),
        };

        return {
            address: decompressEthAddress(contract_address ?? ''),
            blockchain: FloorGameBlockchain.Ethereum,
            standard: EthTokenStardard.Erc721,
            creator,
            name: collectionName ?? '',
        };
    }

    function getCollection(): NftCollection {
        return {
            name: collectionName ?? '',
            thumbnail: collection_image ?? undefined,
            url: getCollectionUrl(),
            slug: getCollectionSlug(),
        };
    }

    function getAsset(): NftAsset {
        // TODO: Pass both `videoUrl` and `imageUrl`.
        return {
            title: tokenName ?? undefined,
            videoUrl: animation_url ?? undefined,
            imageUrl: image ?? undefined,
        };
    }

    function getValue(): NftValue {
        return {
            currency: FloorGameCrypto.Ethereum,
            price: groupCardProps.item?.item_type.Nft.floor_price ?? 0,
            usdPrice:
                legacyConvertCurrency(
                    groupCardProps.item?.item_type.Nft.floor_price ?? 0,
                    Currency.ETH,
                    Currency.USD,
                    groupCardProps.currencyConversionRate,
                ) ?? undefined,
            lastUpdated: parseFloat(groupCardProps.item?.updated_at || '0') || undefined,
        };
    }

    function getMarket(marketplace: FloorGameMarketplace = FloorGameMarketplace.OpenSea): NftMarket {
        switch (marketplace) {
            // OpenSea
            case FloorGameMarketplace.OpenSea:
            default: {
                const contractAddress = decompressEthAddress(contract_address ?? '');
                const buyUrl = `https://opensea.io/assets/ethereum/${contractAddress}/${token_id}`;
                return {
                    marketplace: FloorGameMarketplace.OpenSea,
                    buyUrl,
                };
            }
        }
    }

    function getCurrentTier() {
        const price = getValue().price;
        const trimmedPrice = price ? trimDecimals(price) : 0;
        return determineTier(trimmedPrice);
    }

    return {
        id: getId(),
        tokenId: getTokenId(),
        owner: getOwner(),
        name: getName(),
        contract: getContract(),
        collection: getCollection(),
        currentTier: getCurrentTier(),
        asset: getAsset(),
        value: getValue(),
        status: getStatus(),
        market: getMarket(),
    } as FloorGameCardProps;
}
